import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronRight, Lock, Save, X } from "lucide-react";
import MT_SubfolderRow from "./MT_SubfolderRow";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/DefaultSelectDropdown";

// Data row type
export interface TableDataRow {
  type: "row";
  key: string;
  depth?: number;          // 0 = parent folder, 1+ = subfolder
  folderBadge?: string;    // Subfolder name for badge display
  borderColor?: string;    // Color for left border (hex code)
  isLocked?: boolean;      // If true, row is locked (user is not owner and item is locked)
  ownerId?: string;        // Owner user ID for lock determination
  editPermissions?: 'Open' | 'Locked'; // Edit permissions status
  content: Array<{
    key: string;
    label?: string;        // Optional display label (if different from key)
    fixedX: boolean;
    content: string | boolean | number;
    sectionId?: string;    // For field columns, the section ID
    sectionTitle?: string; // For field columns, the section title
  }>;
}

// Subfolder type
export interface TableSubfolder {
  type: "subfolder";
  key: string;
  folderName: string;
  items: TableDataRow[];
}

// Union type for table items
export type TableItem = TableDataRow | TableSubfolder;

interface MT_DatatableProps {
  data: TableItem[];
  columnWidths: number[];
  selectedRows: Set<string>;
  onRowSelect: (rowKey: string) => void;
  collapsedFolders: Set<string>;
  onFolderToggle: (folderKey: string) => void;
  onCellEditStart?: (
    cellRect: DOMRect,
    value: string,
    cellWidth: number,
    rowKey: string,
    cellKey: string,
    sectionId?: string
  ) => void;
  onBooleanCellChange?: (rowKey: string, cellKey: string, value: boolean) => void;
  isFieldEditable?: (cellKey: string) => boolean;
  // Row click handler - triggered on double click to open item
  onRowClick?: (rowKey: string) => void;
  // Pending changes props
  modifiedItems?: Set<string>; // Set of item IDs that have pending changes
  modifiedCells?: Map<string, string[]>; // Map of itemId -> array of modified cell keys
  onSaveItem?: (itemId: string) => void;
  onDiscardItem?: (itemId: string) => void;
}
const MT_Datatable: React.FC<MT_DatatableProps> = ({ 
  data, 
  columnWidths, 
  selectedRows, 
  onRowSelect,
  collapsedFolders,
  onFolderToggle,
  onCellEditStart,
  onBooleanCellChange,
  isFieldEditable = () => true,
  onRowClick,
  modifiedItems = new Set(),
  modifiedCells = new Map(),
  onSaveItem,
  onDiscardItem,
}) => {
  // Check if a specific cell is modified
  const isCellModified = (rowKey: string, cellKey: string): boolean => {
    const cellKeys = modifiedCells.get(rowKey);
    return cellKeys?.includes(cellKey) ?? false;
  };

  // Check if a row has any modifications
  const isRowModified = (rowKey: string): boolean => {
    return modifiedItems.has(rowKey);
  };

  const handleBooleanChange = (rowKey: string, cellKey: string, value: string) => {
    const boolValue = value === "true";
    onBooleanCellChange?.(rowKey, cellKey, boolValue);
  };

  // Utility to strip HTML tags and get plain text
  const stripHtmlTags = (html: string): string => {
    // Create a temporary element to parse HTML and extract text
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  const handleCellClick = (
    e: React.MouseEvent<HTMLDivElement>,
    rowKey: string,
    cellKey: string,
    value: string | boolean | number,
    cellWidth: number,
    sectionId?: string
  ) => {
    // Only handle text/number cells, not checkboxes
    if (typeof value === "boolean" || !onCellEditStart) return;

    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    
    // Strip HTML tags to get plain text for editing
    const plainTextValue = stripHtmlTags(String(value));
    onCellEditStart(rect, plainTextValue, cellWidth, rowKey, cellKey, sectionId);
  };
  
  // Default width fallbacks
  const DEFAULT_COLUMN_WIDTH = 150;
  const MIN_CHECKBOX_WIDTH = 50;
  
  // Calculate cumulative left positions for sticky columns
  const getStickyLeft = (columnIndex: number): number => {
    let left = 0;
    for (let i = 0; i < columnIndex; i++) {
      left += columnWidths[i] ?? (i === 0 ? MIN_CHECKBOX_WIDTH : DEFAULT_COLUMN_WIDTH);
    }
    return left;
  };

  // Calculate total width for subfolder rows
  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);

  // Render a data row
  const renderDataRow = (row: TableDataRow) => {
    const isSelected = selectedRows.has(row.key);
    const isLocked = row.isLocked === true;
    const rowIsModified = isRowModified(row.key);
    
    // Locked row styles
    const lockedBgClass = isLocked ? 'bg-muted' : 'bg-card';
    const lockedTextClass = isLocked ? 'text-muted-foreground' : '';
    const lockedHoverClass = isLocked ? '' : 'group-hover:bg-muted';
    const lockedCursorClass = isLocked ? 'cursor-not-allowed' : '';
    
    // Modified row styles
    const modifiedRowClass = rowIsModified ? 'row-modified' : '';
    
    return (
      <div
        key={row.key}
        className={`flex items-stretch group min-h-[40px] flex-shrink-0 relative ${lockedCursorClass} ${modifiedRowClass}`}
        title={isLocked ? "This item is locked. Only the owner can edit." : undefined}
        onDoubleClick={() => onRowClick?.(row.key)}
      >
        {/* Row selection checkbox - always sticky */}
        <div
          className={`flex items-center justify-center border border-border p-2 ${lockedHoverClass} overflow-hidden sticky left-0 z-10 ${
            isSelected ? 'bg-primary/10' : lockedBgClass
          }`}
          style={{ 
            width: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
            minWidth: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
            maxWidth: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
            left: 0,
          }}
        >
          <Checkbox 
            checked={isSelected}
            onCheckedChange={() => onRowSelect(row.key)}
            disabled={isLocked}
          />
        </div>

        {/* Data cells */}
        {row.content.map((cell, index) => {
          const isSticky = cell.fixedX;
          const stickyLeft = isSticky ? getStickyLeft(index + 1) : undefined;
          const isBoolean = typeof cell.content === 'boolean';
          const isFirstColumn = index === 0;
          const cellIsModified = isCellModified(row.key, cell.key);
          
          // Apply thick colored left border to first cell
          let leftBorderClass = '';
          let leftBorderStyle = {};
          
          if (isFirstColumn) {
            if (isLocked) {
              // Use grey border for locked items
              leftBorderClass = 'border-l-4 border-l-muted-foreground/50';
            } else if (row.borderColor) {
              // Use custom folder color
              leftBorderClass = 'border-l-4';
              leftBorderStyle = { borderLeftColor: row.borderColor };
            } else {
              // Fallback to default colors
              leftBorderClass = row.depth && row.depth > 0 
                ? 'border-l-4 border-l-primary'  // Primary for subfolder items
                : 'border-l-4 border-l-muted-foreground/50';  // Muted for root folder items
            }
          }
          
          const isCellEditable = isFieldEditable(cell.key);
          const cellCursorClass = isLocked ? 'cursor-not-allowed' : (!isBoolean && isCellEditable ? 'cursor-text' : 'cursor-default');
          
          // Modified cell styling
          const modifiedCellClass = cellIsModified ? 'cell-modified' : '';
          
          // Create unique key using sectionId + field key to avoid duplicates across sections
          const uniqueCellKey = cell.sectionId ? `${cell.sectionId}_${cell.key}` : cell.key;
          
          return (
            <div
              key={uniqueCellKey}
              className={`flex items-center border border-border p-2 ${lockedHoverClass} overflow-hidden ${
                isSelected ? 'bg-primary/10' : lockedBgClass
              } ${isSticky ? 'sticky z-10' : ''} ${cellCursorClass} justify-start ${leftBorderClass} ${lockedTextClass} ${modifiedCellClass}`}
              style={{ 
                width: columnWidths[index + 1] ?? DEFAULT_COLUMN_WIDTH,
                minWidth: columnWidths[index + 1] ?? DEFAULT_COLUMN_WIDTH,
                maxWidth: columnWidths[index + 1] ?? DEFAULT_COLUMN_WIDTH,
                ...(isSticky && { left: stickyLeft }),
                ...leftBorderStyle,
              }} // +1 because checkbox is at index 0
              onClick={(e) => !isBoolean && !isLocked && isCellEditable && handleCellClick(e, row.key, cell.key, cell.content, columnWidths[index + 1] ?? DEFAULT_COLUMN_WIDTH, cell.sectionId)}
            >
              {isBoolean ? (
                <Select
                  value={String(cell.content)}
                  onValueChange={(value) => handleBooleanChange(row.key, cell.key, value)}
                  disabled={isLocked || !isCellEditable}
                >
                  <SelectTrigger className="h-7 w-full border-0 bg-transparent shadow-none focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">True</SelectItem>
                    <SelectItem value="false">False</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <>
                  {/* Show lock icon for first column if locked */}
                  {isFirstColumn && isLocked && (
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <Lock className="h-4 w-4 text-muted-foreground mr-2 flex-shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="z-[100]">
                        <p>This item is locked. Only the owner can edit.</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {/* Show badge on the left for first column if exists */}
                  {isFirstColumn && row.folderBadge && (
                    <span className={`px-2 py-0.5 text-xs rounded-md font-medium border mr-2 flex-shrink-0 ${
                      isLocked 
                        ? 'bg-muted text-muted-foreground border-border' 
                        : 'bg-primary/10 text-primary border-primary/20'
                    }`}>
                      {row.folderBadge}
                    </span>
                  )}
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <div 
                        className={`line-clamp-2 w-full break-words text-left ${lockedTextClass}`}
                        dangerouslySetInnerHTML={{ __html: String(cell.content) }}
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-md break-words z-[100]">
                      <div dangerouslySetInnerHTML={{ __html: String(cell.content) }} />
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          );
        })}
        
        {/* Row action buttons for modified rows */}
        {rowIsModified && (onSaveItem || onDiscardItem) && (
          <div className="flex items-center gap-1 px-2 border border-border bg-card sticky right-0 z-10 row-action-btn">
            {onDiscardItem && (
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDiscardItem(row.key)}
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="z-[100]">Discard changes</TooltipContent>
              </Tooltip>
            )}
            {onSaveItem && (
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSaveItem(row.key)}
                    className="h-7 w-7 p-0 text-green-600 hover:text-green-500 dark:text-green-500 dark:hover:text-green-400 hover:bg-green-500/10"
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="z-[100]">Save changes</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        
        {/* Open item button - shows on hover, absolutely positioned to not affect layout */}
        {onRowClick && (
          <div className={`absolute right-0 top-0 bottom-0 flex items-center bg-card border-l border-border opacity-0 group-hover:opacity-100 transition-opacity z-20 ${rowIsModified ? 'right-[72px]' : ''}`}>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowClick(row.key);
                  }}
                  className="h-full px-2 text-muted-foreground hover:text-primary hover:bg-muted rounded-none"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="z-[100]">Open item</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div>
      {data.map((item) => {
        if (item.type === "subfolder") {
          const isCollapsed = collapsedFolders.has(item.key);
          return (
            <React.Fragment key={item.key}>
              <MT_SubfolderRow
                folderName={item.folderName}
                itemCount={item.items.length}
                isCollapsed={isCollapsed}
                onToggle={() => onFolderToggle(item.key)}
                totalWidth={totalWidth}
              />
              {!isCollapsed && item.items.map(renderDataRow)}
            </React.Fragment>
          );
        } else {
          return renderDataRow(item);
        }
      })}
    </div>
  );
};

export default MT_Datatable;